import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useChatbot } from '../context/ChatbotContext';

const Chatbot = () => {
    const { isDarkMode } = useTheme();
    const { 
        messages, 
        isLoading, 
        isOpen, 
        showOptions,
        sendMessage, 
        toggleChat, 
        clearChat,
        handleOptionClick 
    } = useChatbot();
    const [inputMessage, setInputMessage] = useState('');
    const messagesEndRef = useRef(null);
    const chatWindowRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (inputMessage.trim()) {
            sendMessage(inputMessage);
            setInputMessage('');
        }
    };

    const options = [
        "What is Principal Amount?",
        "Where can I set the price of my dream home?",
        "What does it mean that monthly contribution is added quarterly?"
    ];

    return (
        <div 
            ref={chatWindowRef}
            className={`fixed bottom-4 right-4 w-96 h-[600px] rounded-xl shadow-lg flex flex-col transition-all duration-300 ease-in-out transform ${
                isOpen 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-4 pointer-events-none'
            } ${
                isDarkMode 
                    ? 'bg-gray-800 text-white border border-gray-700' 
                    : 'bg-white text-gray-900 border border-gray-200'
            } z-50`}
        >
            {/* Chat Header */}
            <div className={`p-4 border-b ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <div className="flex justify-between items-center">
                    <h3 className="font-bold">Calculator Assistant</h3>
                    <div className="flex gap-2">
                        <button
                            onClick={clearChat}
                            className={`p-1 rounded transition-colors ${
                                isDarkMode 
                                    ? 'hover:bg-gray-700' 
                                    : 'hover:bg-gray-100'
                            }`}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                        <button
                            onClick={toggleChat}
                            className={`p-1 rounded transition-colors ${
                                isDarkMode 
                                    ? 'hover:bg-gray-700' 
                                    : 'hover:bg-gray-100'
                            }`}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 overscroll-contain">
                {messages.map((message, index) => (
                    <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-lg p-4 mb-4 ${
                            message.role === 'user' 
                                ? 'bg-cyan-500 text-white' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                        }`}>
                            <p className="text-sm">{message.content}</p>
                        </div>
                    </div>
                ))}
                {showOptions && (
                    <div className="flex justify-end">
                        <div className="max-w-[80%] rounded-lg p-4 mb-4 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                            <div className="space-y-2">
                                {options.map((option, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleOptionClick(option)}
                                        className="w-full text-left p-2 rounded-md bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors text-sm border border-gray-200 dark:border-gray-600"
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {isLoading && (
                    <div className="flex justify-start">
                        <div className="max-w-[80%] rounded-lg p-4 bg-gray-100 dark:bg-gray-700">
                            <div className="flex space-x-2">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Form */}
            <form onSubmit={handleSubmit} className={`p-4 border-t transition-colors ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        placeholder="Ask about the calculator..."
                        className={`flex-1 rounded-lg px-4 py-2 transition-colors ${
                            isDarkMode
                                ? 'bg-gray-700 text-white placeholder-gray-400'
                                : 'bg-gray-100 text-gray-900 placeholder-gray-500'
                        }`}
                    />
                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            isDarkMode
                                ? 'bg-cyan-500 hover:bg-cyan-600 text-white'
                                : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                        }`}
                    >
                        Send
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Chatbot; 